
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trophy, Info, BookOpen } from "lucide-react";

// Define FooterLink type
interface FooterLink {
  name: string;
  url: string;
  text: string;
  onClick?: string;
  internal?: boolean;
  isSocial?: boolean;
  icon?: string;
}
interface FooterContent {
  links: FooterLink[];
}

// Declare the global interface for window
declare global {
  interface Window {
    footerContent?: FooterContent;
    showInstructions?: () => void;
  }
}

export const Footer = () => {
  const [links, setLinks] = useState<FooterLink[]>([]);
  
  useEffect(() => {
    // Get footer links from window object if available
    if (typeof window !== 'undefined' && window.footerContent && window.footerContent.links) {
      setLinks(window.footerContent.links);
    }
  }, []);

  // Group links into social and non-social for better organization
  const socialLinks = links.filter(link => link.isSocial);
  const otherLinks = links.filter(link => !link.isSocial);
  
  return (
    <footer className="w-full mt-auto py-4 flex flex-col items-center justify-center space-y-3 text-sm">
      <div className="flex flex-col items-center justify-center space-y-4 px-4 text-sm">
        {/* Regular links - wrap better on mobile */}
        <div className="flex flex-wrap justify-center gap-x-4 gap-y-2 max-w-md mx-auto">
          {otherLinks.map((link, index) => <FooterLinkComponent key={`other-${index}`} link={link} />)}
        </div>
        
        {/* Social links - separate row with more spacing */}
        <div className="flex justify-center gap-x-6 gap-y-2 pt-1">
          {socialLinks.map((link, index) => <FooterLinkComponent key={`social-${index}`} link={link} />)}
        </div>
      </div>
    </footer>
  );
};

// Helper function to render the appropriate icon
const getIcon = (iconName?: string) => {
  if (!iconName) return null;
  
  switch(iconName) {
    case 'trophy':
      return <Trophy className="h-4 w-4 mr-1" />;
    case 'info':
      return <Info className="h-4 w-4 mr-1" />;
    case 'book':
      return <BookOpen className="h-4 w-4 mr-1" />;
    default:
      return null;
  }
};

// Extracted link component to avoid code duplication
const FooterLinkComponent = ({
  link
}: {
  link: FooterLink;
}) => {
  // Determine link style based on whether it's a special feature link
  const linkClasses = link.icon 
    ? "text-[#9b87f5] hover:text-white font-medium text-xs md:text-sm transition-colors hover:scale-105 whitespace-nowrap flex items-center" 
    : "text-gray-400 hover:text-gray-300 text-xs md:text-sm transition-colors hover:scale-105 whitespace-nowrap";
  
  if (link.onClick) {
    return (
      <a 
        href={link.url} 
        onClick={e => {
          e.preventDefault();
          // eslint-disable-next-line no-eval
          eval(link.onClick);
        }} 
        className={linkClasses}
      >
        {getIcon(link.icon)}
        {link.text}
      </a>
    );
  }
  
  if (link.url.startsWith('/') || link.internal) {
    return (
      <Link 
        to={link.url} 
        className={linkClasses}
      >
        {getIcon(link.icon)}
        {link.text}
      </Link>
    );
  }
  
  return (
    <a 
      href={link.url} 
      target="_blank" 
      rel="noopener noreferrer" 
      className={linkClasses}
    >
      {getIcon(link.icon)}
      {link.text}
    </a>
  );
};
